import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { useLang } from "../../lib/LanguageContext";
import { BRAND } from "../../lib/brand";
import { REGISTER_URL } from "../../lib/config";
import { trackRegisterClick } from "../../lib/analytics";
import { Menu, X, Globe } from "lucide-react";

export default function Header() {
  const { lang, t, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#features", label: t.nav.features },
    { href: "#how-it-works", label: t.nav.howItWorks },
    { href: "#ecosystem", label: t.nav.ecosystem },
    { href: "#faq", label: t.nav.faq },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-soft" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src={BRAND.logoIcon} alt="AsiaCommerce" className="h-9 w-9 object-contain" />
          <span className="font-display font-extrabold text-lg tracking-tight text-ink hidden sm:inline">
            Asia<span className="text-brand-red">Commerce</span>
            <span className="ml-1 text-xs align-top bg-brand-orange text-white px-1.5 py-0.5 rounded font-bold">HUB</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-ink-muted hover:text-ink transition-colors"
              data-testid={`nav-${l.href.replace('#','')}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-soft hover:border-brand-red hover:text-brand-red transition-colors text-sm font-semibold"
            aria-label="Toggle language"
          >
            <Globe className="w-4 h-4" />
            <span>{lang.toUpperCase()}</span>
          </button>
          <a href={REGISTER_URL} className="hidden sm:block" rel="noopener" onClick={() => trackRegisterClick("header")}>
            <Button
              data-testid="header-register-btn"
              className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold px-5 h-10"
            >
              {t.nav.register}
            </Button>
          </a>
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-soft px-5 py-4 space-y-3" data-testid="mobile-menu">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setMobileOpen(false)}
              className="block text-base font-medium text-ink py-2"
            >
              {l.label}
            </a>
          ))}
          <a href={REGISTER_URL} onClick={() => setMobileOpen(false)} className="block" rel="noopener">
            <Button className="w-full rounded-full bg-brand-red hover:bg-[#A60D25] text-white" data-testid="mobile-register-btn">
              {t.nav.register}
            </Button>
          </a>
        </div>
      )}
    </header>
  );
}
